"use client"

import { useState } from "react"
import SearchInput from "../../../components/common/SearchInput"
import DownloadChart from "../../../components/common/DownloadChart"
import NoData from "../../../components/common/NoData"
import { BsFiletypeCsv } from "react-icons/bs"
import MenuItem from "../../../components/common/MenuItem"
import { toast } from "react-toastify"
import { Pagination } from "antd"

interface PlayerData {
  name: string
  plays: number
  avgScore: number
}

const PlayersTableSection = ({
  selectedValues,
  chartData = [],
  pageNumber,
  setPageNumber,
  pageSize,
  setPageSize,
  searchTerm,
  setSearchTerm,
  isLoading,
  error,
}: any) => {
  const [sortOrder, setSortOrder] = useState("asc")
  const [sortColumn, setSortColumn] = useState("")

  const searchProps = {
    setSearchTerm,
    setPageNumber,
  }

  const filteredData = chartData.filter((player: PlayerData) =>
    player.name.toLowerCase().includes(searchTerm?.toLowerCase() || "")
  )

  const sortedData = [...filteredData].sort((a: PlayerData, b: PlayerData) => {
    if (!sortColumn) return 0
    const valA = a[sortColumn as keyof PlayerData]
    const valB = b[sortColumn as keyof PlayerData]
    if (typeof valA === "number" && typeof valB === "number") {
      return sortOrder === "asc" ? valA - valB : valB - valA
    } else {
      return sortOrder === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA))
    }
  })

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortOrder("asc")
    }
  }

  const startIndex = (pageNumber - 1) * pageSize
  const paginatedData = sortedData.slice(startIndex, startIndex + pageSize)
  const totalRecords = sortedData.length

  const handlePageChange = (page: number, size: number) => {
    setPageNumber(page)
    setPageSize(size)
  }

  const downloadData = () => {
    toast.loading("Download in progress")

    setTimeout(() => {
      const csvHeader = "Rank,Player Name,Total Plays,Avg Score\n"
      const csvRows = sortedData
        .map(
          (player: PlayerData, index: number) =>
            `${index + 1},"${player.name}",${player.plays},${player.avgScore}`
        )
        .join("\n")

      const csvContent =
        "data:text/csv;charset=utf-8," + encodeURIComponent(csvHeader + csvRows)
      const link = document.createElement("a")
      link.href = csvContent
      link.download = "top-performing-players.csv"
      link.click()

      toast.dismiss()
      toast.success("Download complete")
    }, 800)
  }

  return (
    <div className="mt-4">
      {selectedValues.includes("analyticsReports") && (
        <div className="h-container">
          {/* Header + Tools */}
          <div className="flex flex-wrap lg:flex-nowrap gap-x-72 items-center">
            <div className="mb-2 w-full">
              <h3 className="chart-heading">Top Performing Players</h3>
              <p className="chart-para">Players ranked by engagement and retention</p>
            </div>

            <div className="flex items-center gap-x-3">
              <div className="lg:w-[340px]">
                <SearchInput {...searchProps} placeholder="Search by player name" />
              </div>

              <DownloadChart>
                <div className="w-full text-center">
                  <MenuItem icon={<BsFiletypeCsv />} label="CSV" onClick={downloadData} />
                </div>
              </DownloadChart>
            </div>
          </div>

          {/* Table */}
          <div className="mt-4 overflow-x-auto">
            <table className="table-container">
              <thead className="table-header">
                <tr>
                  <th className="table-head text-left">Rank</th>
                  <th className="table-head text-left cursor-pointer" onClick={() => handleSort("name")}>
                    Player Name {sortColumn === "name" && (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                  <th className="table-head cursor-pointer" onClick={() => handleSort("plays")}>
                    Total Plays {sortColumn === "plays" && (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                  <th className="table-head cursor-pointer" onClick={() => handleSort("avgScore")}>
                    Avg Score {sortColumn === "avgScore" && (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                </tr>
              </thead>

              <tbody className="table-body">
                {isLoading ? (
                  [...Array(pageSize)].map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td className="table-data"><div className="h-4 bg-gray-300 rounded w-8"></div></td>
                      <td className="table-data"><div className="h-4 bg-gray-300 rounded w-32"></div></td>
                      <td className="table-data"><div className="h-4 bg-gray-300 rounded w-16"></div></td>
                      <td className="table-data"><div className="h-4 bg-gray-300 rounded w-16"></div></td>
                    </tr>
                  ))
                ) : error ? (
                  <tr>
                    <td className="table-data" colSpan={4}>
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600">Error loading player data. Please try again.</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((player: PlayerData, index: number) => (
                    <tr key={index}>
                      <td className="table-data">{startIndex + index + 1}</td>
                      <td className="table-data text-left">{player.name}</td>
                      <td className="table-data text-center">{player.plays.toLocaleString()}</td>
                      <td className="table-data text-center">{player.avgScore.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="table-data" colSpan={4}>
                      <NoData header="No Data Available" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRecords > pageSize && (
            <div className="flex justify-end mt-6">
              <Pagination
                current={pageNumber}
                pageSize={pageSize}
                total={totalRecords}
                onChange={handlePageChange}
                showSizeChanger
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PlayersTableSection
